import React, { useEffect, useRef, useState } from "react";
import styles from "./seek.module.scss";
import { Image } from "react-bootstrap";
import cx from "classnames";
import _ from "lodash";
import {
  fetchAnswerForSeek,
  fetchSeekLimit,
  fetchSeekPrompts,
  fetchSeekSessions,
  invalidateSeekSession,
  toggleSeek,
} from "src/redux/actions/seek";
import { useDispatch, useSelector } from "react-redux";
import SeekChat from "./components/chat";
import { SeekSessions } from "./components/sessions";
import Actions from "src/components/actionbar";
import SeekHeader from "./components/seekHeader";
import EmptyMessage from "src/components/emptyMessage";
import { useNavigate, useLocation } from "react-router-dom";
import { useSeekAdoption } from "src/redux/actions/adoption/seek";
import { SEEK } from "src/constants/adoption/sourceTypes";
import { useContentMapAdoption } from "src/redux/actions/adoption/contentMap";
import Text from "src/components/text";
import { nanoid } from "nanoid";
import { SeekAnswerRequest } from "src/types/seek";

const SeekIcon = '/images/seek.svg';

interface KnowledgeContext {
  id: string;
  name?: string;
  entityType: string;
}

interface SeekProps {
  onClose: () => void;
  initialQuery?: string;
  seekAnswer?: string;
  isLimitExceeded?: boolean
}
const Seek: React.FC<SeekProps> = ({ onClose, initialQuery, seekAnswer, isLimitExceeded }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const seekQuery = searchParams.get('query');

  const [isFirstSession, setIsFirstSession] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [seekView, setSeekView] = useState(false);
  const [historyView, setHistoryView] = useState(false);
  const [query, setQuery] = useState("");
  const [limitExpiredView, setLimitExpiredView] = useState(false);
  const [promptsView, setPromptsView] = useState(true);

  const [parentEntity, setParentEntity] = useState<any | undefined>(undefined);

  const prompts = useSelector((state: any) => state.seek.prompts.items);
  const loadingPrompts = useSelector((state: any) => state.seek.prompts.loading);

  const session = useSelector((state: any) => state.seek.session);

  const isSeeking = useSelector((state: any) =>
    _.get(state, "seek.session.loading", false),
  );

  const accountLimit = useSelector((state: any) => state.seek.accountLimit);
  const userLimit = useSelector((state: any) => state.seek.userLimit);

  const seekLimitErrorCode = useSelector((state: any) =>
    _.get(state, "seek.session.error.code", 0),
  );

  const sessionLoading = useSelector((state: any) =>
    _.get(state, "seek.session.loading", false),
  );

  const lastChat = useSelector((state: any) => {
    const chats = _.get(state, "seek.session.item.chats", []);
    return chats[chats.length - 1];
  });

  const knowledgeContext = useSelector((state: any) => {
    if (!parentEntity) {
      return undefined;
    }

    if (parentEntity.entityType === "stream") {
      const streamId = parentEntity.id;
      const stream = _.get(state, `stream.items.${streamId}.item`);
      if (!stream) {
        return null;
      }

      return {
        id: streamId,
        name: stream.name,
        entityType: "stream",
      }
    } else if (parentEntity.entityType === "group") {
      const groupId = parentEntity.id;
      const group = _.get(state, `group.${groupId}.item`);

      if (!group) {
        return null;
      }

      if (!group.stream) {
        return null;
      }

      return {
        id: group.stream.id,
        name: group.stream.name,
        entityType: "stream",
      };
    }
  });

  const { trackSeekNewChatClicked, trackSeekSessionsClicked, trackSeekClicked } = useSeekAdoption();
  const { trackContentSearched } = useContentMapAdoption();

  useEffect(() => {
    if (lastChat && lastChat.answer) {
      dispatch(fetchSeekLimit());
    }
  }, [lastChat?.answer]);

  useEffect(() => {
    dispatch(fetchSeekLimit());
    dispatch(fetchSeekPrompts());
    
    if (!seekAnswer) {
      dispatch(invalidateSeekSession());
    }

    setIsActive(true);
    
    trackSeekClicked({ assetType: "", sourceCta: SEEK })
    
    let timeout = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 1750);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    return () => {
      dispatch(invalidateSeekSession());
    }
  }, [])


  useEffect(() => {
    if (seekView) {
      dispatch(fetchSeekSessions({ page: 0, limit: 15 }));
    }
  }, [seekView]);

  useEffect(() => {
    if (seekLimitErrorCode === 20003) {
      setLimitExpiredView(true);
    }
  }, [seekLimitErrorCode]);

  useEffect(() => {
    if (_.get(session, "item.id")) {
      setPromptsView(false);
    }
  }, [session])

  useEffect(() => {
    if (!loadingPrompts && _.size(prompts) == 0) {
      setPromptsView(false);
    } else {
      setPromptsView(true);
    }
  }, [prompts, loadingPrompts])

  const trackSeekAdoption = (query: string) => {
    trackContentSearched({
      sourceCta: SEEK,
      searchString: query,
      filter: null,
      searchTags: [],
      categories: []
    });
  };

  const handleQuery = (query: string, e?) => {
    if (e) {
      e.preventDefault();
    }

    if (_.isEmpty(query)) {
      return;
    }

    setSeekView(true);
    setIsFirstSession(false);


    const request: SeekAnswerRequest = {
      id: nanoid(6),
      question: query,
      session: session.item.id,
      request: {
        includeActions: true,
      }
    };

    if(knowledgeContext && knowledgeContext.entityType === "stream"){
      request.request.streams = [knowledgeContext.id];
    }

    dispatch(fetchAnswerForSeek(request));
    trackSeekAdoption(query)
    setQuery("");
  };

  const handleClose = () => {
    dispatch(toggleSeek());
  };

  const invalidateSeek = () => {
    setIsFocused(true);
    dispatch(invalidateSeekSession());
  };

  //helper function to get classnames
  const getClassNames = () => {
    return cx(
      styles.seekInputContainer,
      isActive && styles.seekInputActive,
      isSeeking && styles.seeking,
      historyView && styles.historyViewActive,
      !isFirstSession && session.item.id === '' && !sessionLoading && styles.seekInputNewChat,
    );
  };

  const renderPlaceholder = () => {
    if (isSeeking) {
      return "Generating response...";
    } else if (session.item.chats.length > 0) {
      return "Ask a follow up question...";
    } else {
      return "Try asking something...";
    }
  };

  useEffect(() => {
    if (initialQuery) {
      handleQuery(initialQuery);
    }
  }, [initialQuery]);

  useEffect(() => {
    if (seekAnswer && seekQuery) {
      setSeekView(true);
      setIsFirstSession(false);
      trackSeekAdoption(seekQuery);
    }
  }, [seekAnswer, seekQuery]);

  useEffect(() => {
    const path = location.pathname;
    if (!path) {
      return;
    }

    if (path.startsWith("/streams/")) {
      const streamId = path.split("/")[2];
      setParentEntity({
        id: streamId,
        entityType: "stream",
      });
    } else if (path.startsWith("/groups/")) {
      const groupId = path.split("/")[2];
      setParentEntity({
        id: groupId,
        entityType: "group",
      });
    }
  }, [location.pathname]);

  return (
    <div className={cx(styles.seekContainer, seekView && styles.seekView)}>
      {seekView && <SeekChat sessionView={historyView} seekAnswer={seekAnswer} />}
      {seekView && (
        <>
          <div
            className={cx(
              styles.sessionContainer,
            )}
          >
            <div className={styles.headerContainer}>
              <div>
                <SeekHeader />
              </div>
              {historyView ? (
                <i
                  className={cx(
                    styles.closeHistoryViewIcon,
                    "paperflite-dropdown"
                  )}
                  onClick={() => {
                    trackSeekSessionsClicked();
                    setHistoryView(false)
                  }}
                ></i>) : (<></>)}
            </div>
            <div className={cx(styles.seekSessions, styles.sessions, historyView && styles.activeSessions)}>
              <SeekSessions />
            </div>
          </div>
          <div
            className={cx(
              historyView ? styles.seekSeparator : styles.hideSeparator,
            )}
          ></div>
        </>
      )}
      <div className={getClassNames()}>
        {isFocused &&
          _.size(session.item.chats) == 0 &&
          _.isEmpty(query) &&
          promptsView &&
          !limitExpiredView &&
          !sessionLoading &&
          (
            <div className={styles.seekPrompts}>
              {promptsView && _.size(prompts) > 1 && (
                prompts.map((prompt: any, index: number) => (
                  <div
                    key={index}
                    className={styles.seekPrompt}
                    onClick={() => {
                      setQuery(prompt);
                      handleQuery(prompt);
                    }}
                  >
                    {prompt}
                  </div>
                )))}
            </div>
          )}
        <form onSubmit={(e) => handleQuery(query, e)}>
          <div className={styles.seekInputItems}>
            <Image src={SeekIcon} alt="" className={cx(styles.seekIcon, isActive && styles.seekIconActive, seekView && styles.seekIconFocused)} />
            {knowledgeContext &&
              <KnowledgeContextLabel knowledgeContext={knowledgeContext} onRemove={() => {
                setParentEntity(undefined);
              }} />
            }

            <input
              ref={inputRef}
              disabled={isSeeking}
              value={query}
              className={styles.seekInput}
              type="text"
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              placeholder={renderPlaceholder()}
              onFocus={() => setIsFocused(true)}
            />
            <div className={styles.seekInputActionContainer}>
              {
                query ? <i
                  className={cx(
                    "paperflite-right-arrow",
                    styles.cursorPointer,
                    styles.arrowIcon,
                  )}
                  onClick={() => handleQuery(query)}
                ></i> : <></>
              }
              {
                isFirstSession ? <i
                  className={cx(
                    styles.cursorPointer,
                    styles.closeIcon,
                    "paperflite-close",
                  )}
                  onClick={handleClose}
                ></i> : <></>
              }
            </div>
          </div>
        </form>
      </div>
      {seekView && (
        <div className={styles.actionsBar}>
          <Actions.Container>
            <Actions.Row className={cx(styles.newChat)}>
              {!historyView ? (<Actions.Button
                disabled={false}
                className={styles.historyButton}
                icon="paperflite-history"
                title=""
                onClick={() => {
                  trackSeekSessionsClicked();
                  setHistoryView(!historyView);
                }}
              />) : (<></>)}
              <Actions.Button
                disabled={userLimit >= accountLimit}
                className={styles.newChatButton}
                icon="paperflite-add"
                title="start new chat"
                onClick={() => {
                  trackSeekNewChatClicked(SEEK);
                  invalidateSeek();
                }}
              />
              <div className={styles.divider}></div>
              <i onClick={() => onClose()} className={cx(styles.cursorPointer, "paperflite-close")}></i>
            </Actions.Row>
          </Actions.Container>
        </div>
      )}
      {seekView &&
        limitExpiredView &&
        _.size(session.item.chats) <= 0 &&
        !isSeeking && (
          <div className={styles.limitExpired}>
            <EmptyMessage
              width={380}
              image={"/images/seek/tokenExpiry.svg"}
              heading="Looks like you have exhausted all your tokens to SEEK. Please reach out to your admin or Paperflite's CSM to enable this further. In the meanwhile, you can switch to the good old search."
              message=""
              {...(!isLimitExceeded && {
                action: {
                  title: "Go to search",
                  icon: "paperflite-right-arrow",
                  onClick: () => {
                    dispatch(toggleSeek());
                    navigate("/search");
                  },
                },
              })}
            />
          </div>
        )}
    </div>
  );
};

interface KnowledgeContextLabelProps {
  knowledgeContext: KnowledgeContext;
  onRemove?: () => void;
}

export const KnowledgeContextLabel = ({ knowledgeContext, onRemove }: KnowledgeContextLabelProps) => {

  return (
    <div className={styles.knowledgeContextLabel}>
      <span className={styles.label} >
        <Text text={`In: ${knowledgeContext.name || "Knowledge Context"}`} />
      </span>
      {onRemove && (
        <button
          type="button"
          className={styles.remove}
          onClick={onRemove}
          aria-label={`Clear context`}
        >
          <i className="paperflite-show-less" aria-hidden />
        </button>
      )}
    </div>
  );
};

export default Seek;
